// components/Reports.tsx — 2 tabs: Overview + Transactions
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ReportsOverview from './reports/ReportsOverview';
import TransactionsReport from './reports/TransactionsReport';
import { TrendingUp, FileText, BarChart3, Search } from 'lucide-react';
import { useLocalization } from '../contexts/LocalizationContext';

const Reports: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLocalization();

  const getActiveTab = () => {
    const path = location.pathname;
    if (path.includes('/reports/transactions')) return 'transactions';
    return 'overview';
  };

  const activeTab = getActiveTab();

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'transactions', label: 'Transactions', icon: Search },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-br from-purple-500/5 via-blue-500/5 to-indigo-500/10 rounded-2xl p-6 lg:p-7 text-slate-800 shadow-lg border border-purple-200 backdrop-blur-sm">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-5">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-white/80 rounded-lg border border-purple-100 shadow-sm backdrop-blur-sm">
                <TrendingUp className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <h1 className="text-xl lg:text-2xl font-bold text-slate-800">
                  {activeTab === 'overview' ? 'Reports Overview' : 'Transactions'}
                </h1>
                <p className="text-slate-600 mt-1 text-xs lg:text-sm">
                  {activeTab === 'overview'
                    ? "Today's summary and key metrics"
                    : 'Search, filter, and export your transactions'}
                </p>
              </div>
            </div>

            {/* Navigation tabs */}
            <div className="flex flex-wrap gap-2 mt-4">
              {tabs.map((tab) => {
                const TabIcon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => navigate('/reports/' + tab.id)}
                    className={
                      'flex items-center px-4 py-2 rounded-lg transition-all duration-200 text-sm font-medium ' +
                      (isActive
                        ? 'bg-purple-600 text-white shadow-md'
                        : 'bg-white/60 text-slate-700 border border-slate-200 hover:bg-white hover:border-slate-300')
                    }
                  >
                    <TabIcon className={'h-4 w-4 mr-2 ' + (isActive ? 'text-white' : 'text-slate-500')} />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6 lg:p-8">
        {activeTab === 'overview' ? <ReportsOverview /> : <TransactionsReport />}
      </div>
    </div>
  );
};

export default Reports;
